import { useEffect, useState, useCallback } from 'react';
import { Trophy, RefreshCw } from 'lucide-react';
import type { Match } from '@/lib/matches';
import { STAGE_ORDER, isMatchLive, isMatchUpcoming } from '@/lib/matches';
import { PageHeader } from '@/components/common/PageHeader';
import { MatchStageGroup, MatchFilters, TimezoneToggle, WorldCupGroupTable } from '@/components/schedule';
import { MatchDetailModal } from '@/components/schedule/MatchDetailModal';
import type { MatchesResponse } from '@/lib/matches';

const TZ_KEY = 'streampitch:tz';

function getStoredTz(): boolean {
  try {
    return localStorage.getItem(TZ_KEY) === 'utc';
  } catch {
    return false;
  }
}

function setStoredTz(val: boolean) {
  try {
    localStorage.setItem(TZ_KEY, val ? 'utc' : 'local');
  } catch {
    /* localStorage may be blocked */
  }
}

type FilterTab = 'all' | 'live' | 'upcoming' | 'finished';

function filterMatches(matches: Match[], tab: FilterTab): Match[] {
  switch (tab) {
    case 'live': return matches.filter(m => isMatchLive(m));
    case 'upcoming': return matches.filter(m => isMatchUpcoming(m));
    case 'finished': return matches.filter(m => m.status === 'FINISHED');
    default: return matches;
  }
}

/** Group matches by stage (e.g. GROUP_STAGE, LAST_16, etc.) */
function groupByStage(matches: Match[]): Map<string, Match[]> {
  const map = new Map<string, Match[]>();
  for (const m of matches) {
    const key = m.stage || 'OTHER';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  }
  return map;
}

/** Group GROUP_STAGE matches by their group letter */
function groupByGroupLetter(matches: Match[]): Map<string, Match[]> {
  const map = new Map<string, Match[]>();
  for (const m of matches) {
    const group = m.group || '?';
    if (!map.has(group)) map.set(group, []);
    map.get(group)!.push(m);
  }
  return map;
}

export function WorldCupMatchesPage() {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [useUTC, setUseUTC] = useState(getStoredTz);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/data/worldcup-matches.json');
      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
      const data = await res.json() as MatchesResponse;
      setMatches(data.matches || []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setStoredTz(useUTC);
  }, [useUTC]);

  const filtered = filterMatches(matches, filterTab);
  const byStage = groupByStage(filtered);

  // Sort stages by tournament order
  const sortedStages = Array.from(byStage.entries())
    .filter(([, ms]) => ms.length > 0)
    .sort(([a], [b]) => (STAGE_ORDER[a] || 99) - (STAGE_ORDER[b] || 99));

  const groupStageMatches = byStage.get('GROUP_STAGE') || [];
  const groupGroups = groupByGroupLetter(groupStageMatches);
  const sortedGroups = Array.from(groupGroups.entries()).sort(([a], [b]) => a.localeCompare(b));

  const knockoutStages = sortedStages.filter(([stage]) => stage !== 'GROUP_STAGE');

  const counts: Record<string, number> = {
    all: matches.length,
    live: filterMatches(matches, 'live').length,
    upcoming: filterMatches(matches, 'upcoming').length,
    finished: filterMatches(matches, 'finished').length,
  };

  const handleRefresh = () => {
    setLoading(true);
    setRefreshing(true);
    load();
  };

  return (
    <div className="flex flex-col gap-6 h-full">
      <PageHeader
        icon={<Trophy className="w-7 h-7 text-accent" />}
        title="World Cup 2026"
        subtitle={`FIFA World Cup · ${matches.length} matches`}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-md bg-bg-elevated border border-border text-text-muted hover:text-text hover:border-border-hover transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <TimezoneToggle useUTC={useUTC} onChange={setUseUTC} />
          </div>
        }
      />

      <MatchFilters value={filterTab} onChange={setFilterTab as (v: string) => void} counts={counts} />

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-bg-elevated rounded-lg animate-pulse border border-border" />
          ))}
        </div>
      )}

      {error && (
        <div className="text-center py-12 text-text-muted">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Unable to load World Cup schedule</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={() => load()}
            className="mt-4 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && matches.length === 0 && (
        <div className="text-center py-12 text-text-muted">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No World Cup matches available yet</p>
          <p className="text-sm mt-1">Check back closer to the tournament.</p>
        </div>
      )}

      {!loading && !error && matches.length > 0 && (
        <div className="space-y-6">
          {/* Group Stage */}
          {sortedGroups.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-text mb-3 px-1">Group Stage</h2>
              <div className="space-y-2">
                {sortedGroups.map(([group, ms]) => (
                  <WorldCupGroupTable
                    key={group}
                    group={group}
                    matches={ms}
                    onClick={setSelectedMatch}
                    useUTC={useUTC}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Knockout stages */}
          {knockoutStages.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-text mb-3 px-1">Knockout Stage</h2>
              <div className="space-y-3">
                {knockoutStages.map(([stage, ms]) => (
                  <MatchStageGroup
                    key={stage}
                    stage={stage}
                    matches={ms}
                    onClick={setSelectedMatch}
                    useUTC={useUTC}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {selectedMatch && (
        <MatchDetailModal
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          useUTC={useUTC}
        />
      )}
    </div>
  );
}
